import { format, parseISO } from "date-fns";
import { ja } from "date-fns/locale";
import type React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import type { RepeatWithSeat } from "../../hooks/api/useReservations";
import {
  formatDaysOfWeek,
  getNextReservationDate,
} from "../../lib/day-of-week";
import { useDateStore } from "../../stores/dateStore";
import { RecurringSkippedDatesSection } from "./RecurringSkippedDatesSection";

interface RecurringRuleCardProps {
  repeat: RepeatWithSeat;
  onCancelAll: (repeat: RepeatWithSeat) => void;
}

export function RecurringRuleCard({
  repeat,
  onCancelAll,
}: RecurringRuleCardProps) {
  const navigate = useNavigate();
  const setSelectedDate = useDateStore((s) => s.setSelectedDate);
  const nextDate = getNextReservationDate(repeat);
  const seatLabel = `座席 ${repeat.seat?.label ?? repeat.seatId}`;
  const isClickable = !!nextDate;

  const handleClick = () => {
    if (nextDate) {
      setSelectedDate(parseISO(nextDate));
      navigate("/seats");
    }
  };

  return (
    <div
      {...(isClickable
        ? {
            role: "link",
            tabIndex: 0,
            onClick: handleClick,
            onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleClick();
              }
            },
            "aria-label": `${seatLabel} の次回予約日の座席マップを表示`,
          }
        : {})}
      className={[
        "rounded-lg border border-gray-200 bg-white p-4 w-full text-left",
        isClickable &&
          "transition-colors hover:bg-gray-50 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-700 font-medium shrink-0"
            aria-hidden="true"
          >
            {repeat.seatId}
          </div>
          <div className="space-y-1">
            <div className="font-medium text-gray-900">{seatLabel}</div>
            <div className="text-sm text-gray-600">
              毎週 {formatDaysOfWeek(repeat.daysOfWeek)}
            </div>
            <div className="text-sm text-gray-500">
              {format(parseISO(repeat.startDate), "yyyy/MM/dd")}
              {repeat.endDate
                ? ` 〜 ${format(parseISO(repeat.endDate), "yyyy/MM/dd")}`
                : " 〜 無期限"}
            </div>
            {nextDate && (
              <div className="text-sm font-medium text-primary-700">
                次回:{" "}
                {format(parseISO(nextDate), "yyyy年M月d日（E）", {
                  locale: ja,
                })}
              </div>
            )}
            {!nextDate && (
              <div className="text-sm text-gray-500">
                次回の予約はありません
              </div>
            )}
            <RecurringSkippedDatesSection
              repeatId={repeat.repeatId}
              seatLabel={seatLabel}
            />
          </div>
        </div>
        <Button
          variant="danger"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onCancelAll(repeat);
          }}
          aria-label={`座席 ${repeat.seat?.label ?? repeat.seatId} の繰り返し予約をすべてキャンセル`}
        >
          すべてキャンセル
        </Button>
      </div>
    </div>
  );
}
